import { Timestamp } from 'firebase/firestore';
import type { Customer } from '../pages/customers/CustomerInterfaces';
import type { FeedEmailData } from '../pages/mail/components/EmailFeedContent';
import { getEmailFlaggedState } from '../pages/mail/utils/emailFlagState';
import { isEmailFolder, normalizeEmailFolder } from '../pages/mail/utils/emailFolder';

/** Parse the source message date. An ingestion timestamp is never a substitute. */
export function getCorrespondenceDate(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') return null;
  let date: Date;
  if (value instanceof Date) date = value;
  else if (typeof value === 'object') {
    const timestamp = value as Record<string, unknown>;
    if (typeof timestamp.toDate === 'function') {
      const parsed: unknown = timestamp.toDate();
      if (!(parsed instanceof Date)) return null;
      date = parsed;
    } else if (typeof timestamp.seconds === 'number') date = new Date(timestamp.seconds * 1000);
    else return null;
  }
  else if (typeof value === 'string') date = new Date(value);
  else if (typeof value === 'number') date = new Date(value < 1e12 ? value * 1000 : value);
  else return null;
  return Number.isFinite(date.getTime()) ? date : null;
}

export function correspondenceTimestamp(email: { date?: unknown }): number {
  return getCorrespondenceDate(email.date)?.getTime() || 0;
}

function address(value: any): string {
  const raw = typeof value === 'string' ? value : value?.address || value?.email || value?.text || '';
  const match = String(raw).match(/<([^>]+)>/);
  return (match?.[1] || String(raw)).trim().toLowerCase();
}

function recipients(value: any): string[] {
  return (Array.isArray(value) ? value : value ? [value] : []).map(address).filter(Boolean);
}

export function customerCorrespondenceAddresses(customer?: Customer): string[] {
  return Array.from(new Set([customer?.email, ...(customer?.contacts || []).map(c => c.email)]
    .map(address).filter(Boolean))).sort();
}

export function matchesCorrespondenceCustomer(email: FeedEmailData, customerId: string, addresses: readonly string[]): boolean {
  if (email.customerId === customerId) return true;
  const known = new Set(addresses);
  return [...recipients(email.from), ...recipients(email.to), ...recipients(email.cc)]
    .some(value => known.has(value));
}

/** Shared mapping for the client timeline and support board; original IDs open EmailPanel. */
export function convertEmailDoc(id: string, data: Record<string, any>): FeedEmailData {
  const subject = String(data.subject || data.title || '(no subject)');
  const from = (Array.isArray(data.from) ? data.from : data.from ? [data.from] : [])
    .map((item: any) => ({ address: address(item), name: String(item?.name || ''), text: String(item?.text || '') }));
  const out = data.direction === 'out' || data.emailType === 'sent' || data.folder === 'sent';
  const date = getCorrespondenceDate(data.date);
  const text = String(data.textContent || data.text || data.bodyPreview || data.message || '');
  // A stored folder wins (incl. archive/filed: mail the owner filed into an Outlook
  // folder is still correspondence, it just must not read as "inbox"). The
  // deleted:true + folder:'inbox' mislabel normalizes to 'deleted', matching the feed.
  const folder = (isEmailFolder(data.folder) ? normalizeEmailFolder(data) : out ? 'sent' : 'inbox') as FeedEmailData['folder'];
  return {
    id, emailId: id, title: subject, subject, message: text, text, html: data.html || null,
    date: date ? Timestamp.fromDate(date) : Timestamp.fromMillis(0),
    createdAt: date || new Date(0),
    dismissed: !!data.dismissed, deleted: !!data.deleted, pinned: getEmailFlaggedState(data),
    sender: from[0]?.name || from[0]?.address || data.senderEmail || data.sender || '',
    senderEmail: data.senderEmail, emailType: out ? 'sent' : 'inbox', folder,
    from, to: recipients(data.to), cc: recipients(data.cc), bcc: recipients(data.bcc),
    attachments: data.attachments || [],
    customerId: Array.isArray(data.customerId) ? data.customerId[0] : data.customerId || null,
    source: data.source === 'graph' || data.source === 'imap' ? data.source : undefined,
    mailbox: data.mailbox, graph: data.graph, isRead: data.isRead,
    aiSummary: data.aiSummary, aiImportance: data.aiImportance,
    workRelated: data.workRelated, quarantined: data.quarantined,
    linkedJobId: data.linkedJobId || null, linkedJobLabel: data.linkedJobLabel || null,
  };
}

/** Source-linked snapshots take precedence over a potentially stale inbox cache. */
export function mergeCorrespondence(sources: readonly (readonly FeedEmailData[])[]): FeedEmailData[] {
  const byId = new Map<string, FeedEmailData>();
  for (const source of sources) for (const email of source) {
    if (!email.id || byId.has(email.id)) continue;
    byId.set(email.id, email);
  }
  return Array.from(byId.values())
    .filter(email => !email.deleted && email.folder !== 'deleted' && email.folder !== 'drafts' && email.folder !== 'junk')
    .sort((a, b) => correspondenceTimestamp(b) - correspondenceTimestamp(a) || a.id.localeCompare(b.id));
}
